import React, { useEffect, useState } from 'react';
import { X, Check, Clock } from 'lucide-react';
import { useCurrencyConversion } from '../hooks/useCurrencyConversion';
interface UpsellPopupProps {
  isOpen: boolean;
  onClose: () => void; // Handles "No thanks" or close button -> Original Cart
  onAccept: () => void; // Handles "Yes" -> Bundle Cart
}
export function UpsellPopup({
  isOpen,
  onClose,
  onAccept
}: UpsellPopupProps) {
  const [timeLeft, setTimeLeft] = useState(5 * 60); // 5 minutes in seconds
  const {
    localPrice,
    currencyCode,
    countryCode,
    isLoading: currencyLoading
  } = useCurrencyConversion(9.99);
  // Convert 2-letter country code to flag emoji
  const countryFlag = countryCode ? String.fromCodePoint(...countryCode.toUpperCase().split('').map(c => 0x1f1e6 + c.charCodeAt(0) - 65)) : null;
  useEffect(() => {
    if (!isOpen) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onClose(); // Auto-close and go to original cart when time expires
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const formatTime = (n: number) => n.toString().padStart(2, '0');
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-159">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-160" />

      {/* Modal Card */}
      <div className="relative bg-[#E0F2F1] w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in duration-300" data-id="element-161">
        {/* Close Button */}
        <button onClick={onClose} className="absolute top-3 right-3 p-2 bg-white/50 hover:bg-white rounded-full text-gray-500 transition-colors z-10" aria-label="Cerrar oferta" data-id="element-162">
          <X className="w-5 h-5" data-id="element-163" />
        </button>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-6 md:p-8 custom-scrollbar" data-id="element-164">
          {/* Header Section */}
          <div className="text-center mb-6" data-id="element-165">
            <div className="inline-block bg-red-500 text-white px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wider mb-3 shadow-sm animate-pulse" data-id="element-166">
              🔥 ¡Oferta Única! 🔥
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 leading-tight mb-2" data-id="element-167">
              ¡Espera! Tenemos algo especial para ti
            </h2>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed" data-id="element-168">
              Solo por los próximos{' '}
              <span className="font-bold text-red-500" data-id="element-169">5 minutos</span>, agrega
              el MegaKit de Crianza Positiva a tu compra
            </p>
          </div>

          {/* Countdown Timer */}
          <div className="flex justify-center mb-8" data-id="element-170">
            <div className="bg-white border-2 border-red-100 rounded-xl px-6 py-3 shadow-sm flex items-center gap-3" data-id="element-171">
              <Clock className="w-6 h-6 text-red-500 animate-pulse" data-id="element-172" />
              <div className="text-3xl font-mono font-bold text-red-600 tracking-wider" data-id="element-173">
                {formatTime(minutes)}:{formatTime(seconds)}
              </div>
            </div>
          </div>

          {/* Product Images */}
          <div className="relative mb-8" data-id="element-174">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg border-4 border-white" data-id="element-175">
              <img src="https://cdn.magicpatterns.com/uploads/mF53aeAFsBGUdBfJGzkiU5/pasted-image.png" alt="Niña usando rutinas visuales" className="w-full h-full object-cover" data-id="element-176" />
            </div>
            {/* Floating Box Image */}
            <div className="absolute -bottom-6 -right-2 w-32 md:w-40 transform rotate-3 drop-shadow-xl" data-id="element-177">
              <img src="https://cdn.magicpatterns.com/uploads/pAgiBBCtGeFhMoNgkoyNfX/png_kit.png" alt="Caja MegaKit Crianza Positiva" className="w-full h-auto" data-id="element-178" />
            </div>
          </div>

          {/* What's Included */}
          <div className="bg-white rounded-2xl p-5 md:p-6 shadow-sm mb-6 mt-4" data-id="element-179">
            <h3 className="font-bold text-gray-800 mb-4 text-lg" data-id="element-180">
              ¿Qué incluye este MegaKit?
            </h3>
            <ul className="space-y-3" data-id="element-181">
              {['+10 Rutinas visuales listas para imprimir', 'Checklist diario editable', 'Tablero de rutinas estilo juego (4 versiones)', 'Control del tiempo visual con reloj en modo juego', 'Cuentos con emociones y valores', 'Guías prácticas para corregir sin gritar', 'Planificador emocional + lista de gratitud'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-182">
                  <div className="mt-0.5 bg-green-100 rounded-full p-0.5 shrink-0" data-id="element-183">
                    <Check className="w-4 h-4 text-green-600" strokeWidth={3} data-id="element-184" />
                  </div>
                  <span className="text-gray-700 text-sm font-medium" data-id="element-185">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* Full Showcase Image */}
          <div className="mb-6 rounded-xl overflow-hidden shadow-md" data-id="element-186">
            <img src="https://cdn.magicpatterns.com/uploads/2cCVaN7HZN7vwJQ4xDhywb/Transforma_gritos_y_berrinches_en_rutinas_de_paz_con_este_metodo_visual_en_menos_de_7_dias_4_20301690-a112-4497-86b1-18393e0d7021_(1).png" alt="Contenido completo del kit" className="w-full h-auto" data-id="element-187" />
          </div>

          {/* Pricing */}
          <div className="text-center mb-8 bg-[#FFF3E0] rounded-xl p-4 border border-orange-100" data-id="element-188">
            <p className="text-gray-500 text-sm mb-1" data-id="element-189">
              Precio normal:{' '}
              <span className="line-through decoration-red-500" data-id="element-190">$29 USD</span>
            </p>
            <p className="text-gray-800 font-bold text-lg" data-id="element-191">
              Hoy con tu compra:{' '}
              <span className="text-3xl text-green-600 font-extrabold" data-id="element-192">
                $9.99 USD
              </span>
            </p>
            {!currencyLoading && localPrice && currencyCode && <div className="mt-2" data-id="element-193">
                <p className="text-sm text-gray-500" data-id="element-194">
                  {countryFlag && <span className="mr-1" data-id="element-195">{countryFlag}</span>}≈{' '}
                  <span className="font-semibold text-gray-700" data-id="element-196">
                    {localPrice}
                  </span>
                  <span className="text-xs text-gray-400 ml-1" data-id="element-197">(estimado)</span>
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5" data-id="element-198">
                  💳 El cobro se realiza en tu moneda local
                </p>
              </div>}
          </div>

          {/* CTAs */}
          <div className="space-y-4" data-id="element-199">
            <button onClick={onAccept} className="w-full bg-[#4CAF50] hover:bg-[#43A047] text-white text-lg md:text-xl font-bold py-4 px-6 rounded-xl shadow-lg transform transition-all duration-200 hover:-translate-y-1 hover:shadow-xl active:scale-95 flex items-center justify-center gap-2 group" data-id="element-200">
              <span data-id="element-201">¡SÍ, AGREGAR A MI PEDIDO!</span>
              <span className="group-hover:translate-x-1 transition-transform" data-id="element-202">
                👉
              </span>
            </button>

            <button onClick={onClose} className="w-full text-gray-500 text-sm hover:text-gray-700 hover:underline transition-colors py-2" data-id="element-203">
              No gracias, solo quiero el MegaKit Lectura Feliz →
            </button>
          </div>
        </div>
      </div>
    </div>;
}